import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { Popup } from 'devextreme-react/popup'
import { Column } from 'devextreme-react/data-grid'
import { SelectInput } from 'components/forms'
import { BaseForm } from 'components'
import { Form, Field, useFormikContext } from 'formik'
import Button from 'devextreme-react/button'
import { useTranslation } from 'react-i18next'

import DataGrid, {
    FilterRow,
    LoadPanel,
    Paging,
    Selection,
    Scrolling,
    RowDragging,
} from 'devextreme-react/data-grid'

import './base-relation-popup.scss'

type RelationPopupProps = {
    availableGridColumns: any, 
    relatedGridColumns: any, 
    relatedData: any[], 
    setRelatedData: any, 
    availableData: any[], 
    setAvailableData: any, 
    loading: boolean, 
    onStoreData: () => void, 
    visible: boolean, 
    setVisiblePopup: any, 
    title?: any,
    filter?: (e: any) => void,
    availableGridRef: any,
    relatedGridRef: any,
    setActiveGrid?: any,
    infoText?: string,
    availableGridTitle?: string,
    relatedGridTitle?: string
}

const AutoUpdate = ({ filter }: any) => {
    const { values }: any = useFormikContext()

    useEffect(() => {
        filter(values)
    },[values, filter])

    return null
}
 
export const IxBaseRelationPopup = ({ 
    availableGridColumns, 
    relatedGridColumns, 
    relatedData, 
    setRelatedData, 
    availableData, 
    setAvailableData, 
    loading, 
    onStoreData, 
    visible, 
    setVisiblePopup, 
    title,
    filter,
    availableGridRef,
    relatedGridRef,
    setActiveGrid,
    infoText,
    availableGridTitle,
    relatedGridTitle
}: RelationPopupProps) => {
    const [selectedAvailableItems, setSelectedAvailableItems] = useState<any>([])
    const [selectedRelatedItems, setSelectedRelatedItems] = useState<any>([])
    const [visibleWarningPopup, setVisibleWarningPopup] = useState(false)
    const [switchedAllDataGrid, setSwitchedAllDataGrid] = useState(false)
    const allDataGridRef = useRef(null)
    const { t } = useTranslation()

    useEffect(() => {
        if (loading) {
            /* v8 ignore next */
            relatedGridRef.current?.instance() && relatedGridRef.current.instance().beginCustomLoading('loading')
            availableGridRef.current?.instance() && availableGridRef.current.instance().beginCustomLoading('loading')
        } else {
            /* v8 ignore next */
            relatedGridRef.current?.instance() && relatedGridRef.current.instance().endCustomLoading()
            availableGridRef.current?.instance() && availableGridRef.current.instance().beginCustomLoading('loading')
        }
    }, [loading, relatedGridRef, availableGridRef])

    const filterOptions = useMemo(() => ([
        {name: t('global:relationBase.filter.allUser'), value: 'all'},
        {name: t('global:relationBase.filter.activeUser'), value: 'active'},
        {name: t('global:relationBase.filter.deactivatedUser'), value: 'deactivated'}
    ]),[t])

    const onMoveToRelatedGgrid = useCallback((e: any) => {
        if (!loading) {
            let selectedRows = e.itemData ? Array.isArray(e.itemData) ? e.itemData : [e.itemData] : selectedAvailableItems
            if (selectedRows.filter((row: any) => row.USER_ID < 2).length) {
                selectedRows = selectedRows.filter((row: any) => row.USER_ID > 1)
                setVisibleWarningPopup(true)
            }
            const filteredData: any = availableData.filter((availableItem: any) =>
                !selectedRows.find((row: any) => availableItem.HDR_ID === row.HDR_ID)
            )

            setRelatedData([...relatedData, ...selectedRows])
            setAvailableData(filteredData)
        }

    },[setRelatedData, selectedAvailableItems, availableData, relatedData, setAvailableData, loading])

    const onMoveToAvailableGrid = useCallback((e: any) => {
        if (!loading) {
            const selectedRows = e.itemData ? Array.isArray(e.itemData) ? e.itemData : [e.itemData] : selectedRelatedItems
            const filteredData: any = relatedData.filter((relatedItem: any) => 
                !selectedRows.find((row: any) => relatedItem.HDR_ID === row.HDR_ID)
            )
    
            setAvailableData([...availableData, ...selectedRows])
            setRelatedData(filteredData)
        }

    },[setRelatedData, selectedRelatedItems, availableData, relatedData, setAvailableData, loading])

    const moveAllDataToAvailableGrid = useCallback(() => {
        if (!loading) {
            setAvailableData([...availableData, ...relatedData])
            setRelatedData([])
        }

    },[availableData, relatedData, setAvailableData, setRelatedData, loading])

    const moveAllDataToRelatedGgrid = useCallback(() => {
        if (!loading) {
            if (availableData.filter((availableItem: any) => availableItem.USER_ID < 2).length) {
                const filteredAvailableData = availableData.filter((availableItem: any) => availableItem.USER_ID > 1)
                setVisibleWarningPopup(true)
                setRelatedData([...relatedData, ...filteredAvailableData])
                setAvailableData(availableData.filter((availableItem: any) => availableItem.USER_ID < 2))
            }
            else {
                setRelatedData([...relatedData, ...availableData])
                setAvailableData([])
            }
        }

    },[availableData, relatedData, setRelatedData, setAvailableData, loading])

    const onDragStart = useCallback((e: any) => {
        if (!loading) {
            const selectedData: any[] = e.component.getSelectedRowsData()
            if (selectedData.length) { e.itemData = selectedData }
        }

    }, [loading])

    useEffect(() => {
        if (switchedAllDataGrid) {
            setTimeout(() => {
                const grid: any = allDataGridRef.current
                grid?.instance().selectRows([...relatedData].map((item: any) => item.HDR_ID), true)
                setActiveGrid && setActiveGrid(grid)
            }, 0)
        }
        else {setActiveGrid && setActiveGrid(availableGridRef.current)}
        

    },[selectedRelatedItems, allDataGridRef, availableGridRef, switchedAllDataGrid, setActiveGrid])

    const onSelectionChanged = useCallback((data: any) => {
        let selectedRows = data.selectedRowsData
        selectedRows.forEach((row: any) => {
            if (row.USER_ID < 2) {
                const grid: any = allDataGridRef.current
                grid?.instance().deselectRows([row.HDR_ID])
                selectedRows = selectedRows.filter((item: any) => item.HDR_ID !== row.HDR_ID)
                setVisibleWarningPopup(true)
            }
        })
        const filteredData = [...availableData, ...relatedData].filter((item: any) => !selectedRows.find((row: any) => row.HDR_ID === item.HDR_ID))
        setAvailableData(filteredData)
        setRelatedData(selectedRows)
    },[availableData, relatedData, setAvailableData, setRelatedData])

    const onToggleSelectAll = useCallback((action: string) => {

        return () => {
            const grid: any = allDataGridRef.current
            const toggle: any = {
                select: () => {grid?.instance().selectAll()},
                deselect: () => {grid?.instance().deselectAll()}
            }
    
            toggle[action]()
        }
    },[])

    const formik = {
        initialValues: {userFilter: 'all'},
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },
    }

    return (
        <div>
            <Popup
                visible={visibleWarningPopup}
                showTitle={true}
                title='Warning'
                width={300}
                height='auto'
                showCloseButton={true}
                onHiding={() => setVisibleWarningPopup(false)}
            >
                <p>User with username 'admin' or 'ixsql' cannot be removed!</p>
                <Button className='warning-ok-btn' text={t('global:button.ok')} type='default' onClick={() => setVisibleWarningPopup(false)}/>
            </Popup>
            <Popup
                visible={visible}
                onHiding={() => setVisiblePopup(false)}
                showTitle={true}
                title={title}
                showCloseButton={true}
                width='auto'
                wrapperAttr={{class: 'relation-popup'}}
                height='auto'
            >
                {infoText && <p>{infoText}</p>}
                {
                    filter &&
                        <BaseForm {...formik}>
                            {() => (
                                <Form>
                                    <AutoUpdate filter={filter}/>
                                    <Field
                                        opts={filterOptions}
                                        as={SelectInput}
                                        name='userFilter'
                                        label={t('global:relationBase.filter.label')}
                                    />
                                </Form>
                            )}
                        </BaseForm>
                }
                {
                    switchedAllDataGrid
                        ? (
                        <div style={{width: '850px'}}>
                            <DataGrid
                                ref={allDataGridRef}
                                keyExpr={'HDR_ID'}
                                dataSource={[...availableData, ...relatedData].sort((a, b) => a.HDR_ID - b.HDR_ID)}
                                height={500}
                                onSelectionChanged={onSelectionChanged}
                                columnAutoWidth={true}
                                width='100%'
                                columnMinWidth={100}
                                columnResizingMode='widget'
                                showBorders={true}
                                showColumnLines={true}
                            >
                                {availableGridColumns.map((col: any) => (<Column dataField={col.dataField} key={col.dataField} caption={col.caption} alignment='right'/>))}
                                <Paging enabled={false}/>
                                <LoadPanel
                                    enabled={loading}
                                    showIndicator={true}
                                />
                                <Selection
                                    mode='multiple'
                                    showCheckBoxesMode='onClick'
                                    deferred={false}
                                />
                                <Scrolling mode='virtual' scrollByContent={true} />
                                <FilterRow visible={true} />
                            </DataGrid>
                        </div>
                        )
                        : (
                        <div className='grids-section' >
                            <div className='grid'>
                                {availableGridTitle && <p>{availableGridTitle}</p>}
                                <DataGrid
                                    ref={availableGridRef}
                                    dataSource={availableData}
                                    height={500}
                                    onSelectionChanged={(data: any) => setSelectedAvailableItems(data.selectedRowsData)}
                                    columnAutoWidth={true}
                                    width='100%'
                                    columnMinWidth={100}
                                    columnResizingMode='widget'
                                    showBorders={true}
                                    showColumnLines={true}
                                >
                                    {availableGridColumns.map((col: any) => (<Column dataField={col.dataField} key={col.dataField} caption={col.caption} alignment='right'/>))}
                                    <Paging enabled={false}/>
                                    <RowDragging
                                        onRemove={onMoveToRelatedGgrid} 
                                        onDragStart={onDragStart}
                                        group='tasksGroup'
                                    />
                                    <LoadPanel
                                        enabled={loading}
                                        showIndicator={true}
                                    />
                                    <Selection
                                        mode='multiple'
                                        showCheckBoxesMode='onClick'
                                        deferred={false}
                                    />
                                    <Scrolling mode='virtual' scrollByContent={true} />
                                    <FilterRow visible={true} />
                                </DataGrid>
                            </div>
                            <div className='arrow-buttons'>
                                <div>
                                    <i className='dx-icon-chevrondoubleright' onClick={moveAllDataToRelatedGgrid} title={t('global:relationBase.buttons.assignAll')}/>
                                </div>
                                <div>
                                    <i className='dx-icon-chevronright' onClick={onMoveToRelatedGgrid} title={t('global:relationBase.buttons.assingSelected')}/>
                                </div>
                                <div>
                                    <i className='dx-icon-chevronleft' onClick={onMoveToAvailableGrid} title={t('global:relationBase.buttons.removeSelected')}/>
                                </div>
                                <div>
                                    <i className='dx-icon-chevrondoubleleft' onClick={moveAllDataToAvailableGrid} title={t('global:relationBase.buttons.removeAll')}/>
                                </div>
                            </div>
                            <div className='grid'>
                                {relatedGridTitle && <p>{relatedGridTitle}</p>}
                                <DataGrid
                                    ref={relatedGridRef}
                                    dataSource={relatedData}
                                    height={500}
                                    onSelectionChanged={(data: any) => setSelectedRelatedItems(data.selectedRowsData)}
                                    columnAutoWidth={true}
                                    width='100%'
                                    columnMinWidth={100}
                                    columnResizingMode='widget'
                                    showBorders={true}
                                    showColumnLines={true}
                                >
                                    {relatedGridColumns.map((col: any) => (<Column dataField={col.dataField} key={col.dataField} caption={col.caption} alignment='right'/>))}
                                    <Paging enabled={false}/>
                                    <RowDragging
                                        onRemove={onMoveToAvailableGrid} 
                                        onDragStart={onDragStart}
                                        group='tasksGroup'
                                    />
                                    <LoadPanel
                                        enabled={loading}
                                        showIndicator={true}
                                    />
                                    <Selection
                                        mode='multiple'
                                        showCheckBoxesMode='onClick'
                                        deferred={false}
                                    />
                                    <Scrolling mode='virtual' scrollByContent={true} />
                                    <FilterRow visible={true} />
                                </DataGrid>
                            </div>
                        </div>
                        )
                }

                <div className='buttons'>
                    {switchedAllDataGrid && (
                        <div className='select-buttons'>
                            <Button 
                                text={t('global:relationBase.buttons.selectAll')} 
                                onClick={onToggleSelectAll('select')}
                            />
                            <Button 
                                text={t('global:relationBase.buttons.deselectAll')}
                                onClick={onToggleSelectAll('deselect')}
                            />
                        </div>
                    )
                        
                    }
                    <Button text={t('global:relationBase.buttons.switchView')} onClick={() => {setSwitchedAllDataGrid(!switchedAllDataGrid)}} disabled={!availableData.length && !relatedData.length}/>
                        
                    <div className='confirm-buttons' >
                        <Button text={t('global:button.ok')} onClick={onStoreData} type='default'/>
                        <Button text={t('global:button.cancel')} onClick={() => setVisiblePopup(false)} type='normal'/>
                    </div>
                </div>
            </Popup>
        </div>

    )
}
